import { useState } from "react"
import { t } from "@lingui/core/macro"
import { error } from "@multimediallc/logging"
import { useParams } from "react-router-dom"
import { usePerformToggle } from "../../../hooks/appContext"
import { useAppDispatch } from "../../../store/hooks"
import {
    useGetIgnoredUsersQuery,
    useIgnoreMutation,
    useUnignoreMutation,
} from "../../../store/ignoredUsersSlice"
import {
    removeThread,
    useDeleteConversationMutation,
} from "../../../store/messagingSlice"
import { addPageAction } from "../../../utils/newrelic"
import {
    CloseBlue,
    EditNote,
    EllipsisVertical,
    Ignore,
    MinimizeBlue,
    Mute,
    Profile,
    Report,
    Trash,
} from "../../common/atoms/Icons/Chat"
import {
    ConfirmModal,
    ModalButtonColor,
} from "../../common/molecules/ConfirmModal/ConfirmModal"
import { IconButton } from "../common/IconButton/IconButton"
import { UnreadBadge } from "../common/UnreadBadge/UnreadBadge"
import { DM_WINDOW_LOCATION, INBOX_VERSION_V2 } from "../constants"
import { useMessagingView } from "../hooks"
import { HorizontalPosition, Menu, MenuItem, VerticalPosition } from "../Menu"
import { MenuItemColor } from "../Menu/MenuItem"
import { useMessagingContext } from "../MessagingContext"
import { classNames, handleProfileNavigation } from "../utils"
import styles from "./MessageHeaderMenu.module.scss"

type Props = {
    onMinimize: () => void
    onConversationDeleted: () => void
    onReportClick: () => void
    isReportEnabled: boolean
    onNoteClick: () => void
    numUnread?: number
}

export function MessageHeaderMenu({
    onMinimize,
    onConversationDeleted,
    onReportClick,
    isReportEnabled,
    onNoteClick,
    numUnread = 0,
}: Props) {
    const dispatch = useAppDispatch()
    const { onCloseMessaging, isMinimized } = useMessagingContext()
    const [isOpen, setIsOpen] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const messagingView = useMessagingView()
    const { username = "" } = useParams<{ username: string }>()
    const [ignore] = useIgnoreMutation()
    const [deleteConversation] = useDeleteConversationMutation()
    const [unignore] = useUnignoreMutation()
    const { data: ignoredUsersData } = useGetIgnoredUsersQuery()
    const isIgnored = (ignoredUsersData?.users || []).includes(username)
    const isFutureRlsEnabled = usePerformToggle("MsgFutureRls").active
    const isDesktop = !messagingView.isMobile

    const handleMenuOpenChange = (open: boolean) => {
        setIsOpen(open)
        if (open || (!open && isOpen)) {
            addPageAction("DmConversationHeaderMenu", {
                actionType: open ? "open" : "close",
            })
        }
    }

    const onIgnoreMenuClick = async (): Promise<void> => {
        setIsOpen(false)
        if (isIgnored) {
            try {
                await unignore({ username }).then(() => {
                    addPageAction("UnignoreUser", {
                        to_user: username,
                        list_size: ignoredUsersData?.users.length,
                        source: DM_WINDOW_LOCATION,
                        inboxVersion: INBOX_VERSION_V2,
                    })
                })
            } catch (e) {
                error("Failed to unignore user:", e)
            }
        } else {
            try {
                await ignore({ username }).then(() => {
                    addPageAction("IgnoreUser", {
                        to_user: username,
                        list_size: ignoredUsersData?.users.length,
                        source: DM_WINDOW_LOCATION,
                        inboxVersion: INBOX_VERSION_V2,
                    })
                })
            } catch (e) {
                error("Failed to ignore user:", e)
            }
        }
    }

    const onDeleteConversationClick = () => {
        setIsOpen(false)
        setShowDeleteModal(true)
    }

    const handleConfirmDelete = async (): Promise<void> => {
        setShowDeleteModal(false)

        try {
            await deleteConversation({ to_username: username })
            dispatch(removeThread({ username }))
            onConversationDeleted()
        } catch (e) {
            error("Failed to delete conversation:", e)
        }
    }

    const handleProfileClick = () => {
        addPageAction("DmViewProfile")
        setIsOpen(false)
        handleProfileNavigation({
            username,
            isDesktop: messagingView.isDesktopConversation,
        })
    }

    const handleNoteClick = () => {
        addPageAction("DmUserNote", {
            actionType: "open",
        })
        setIsOpen(false)
        onNoteClick()
    }

    const handleReportClick = () => {
        if (!isReportEnabled) {
            // Do nothing if there is no message to report
            return
        }
        addPageAction("DmReportUser", {
            actionType: "open",
        })
        setIsOpen(false)
        onReportClick()
    }

    const menuToggleButton = (
        <IconButton
            icon={<EllipsisVertical height={20} width={20} color="#222429" />}
            iconAlt="Menu"
            className={classNames(styles.menuButton, styles.menuIcon, {
                [styles.open]: isOpen,
            })}
            data-testid="message-header-menu-button"
        />
    )

    return (
        <div
            className={classNames(styles.container, {
                [styles.desktop]: isDesktop,
            })}
            data-testid="message-header-menu"
        >
            <div
                className={classNames(styles.iconsContainer, {
                    [styles.minimized]: isMinimized,
                })}
            >
                {!isMinimized && (
                    <>
                        <Menu
                            toggle={menuToggleButton}
                            isOpen={isOpen}
                            onOpenChange={handleMenuOpenChange}
                            toggleClassName={styles.menuToggleWrapper}
                            verticalPosition={VerticalPosition.Bottom}
                            horizontalPosition={HorizontalPosition.Left}
                            data-testid="message-header-menu-container"
                        >
                            <MenuItem
                                icon={<Profile height={20} width={20} />}
                                text={t`View Profile`}
                                onClick={handleProfileClick}
                                data-testid="view-profile-menu-item"
                            />
                            {isFutureRlsEnabled && (
                                <MenuItem
                                    icon={<Mute height={20} width={20} />}
                                    text={t`Mute`}
                                    onClick={() => {
                                        setIsOpen(false)
                                    }}
                                    data-testid="mute-menu-item"
                                />
                            )}
                            <MenuItem
                                icon={<EditNote height={20} width={20} />}
                                text={t`Note`}
                                onClick={handleNoteClick}
                                data-testid="note-menu-item"
                            />
                            <MenuItem
                                icon={<Ignore height={20} width={20} />}
                                text={
                                    isIgnored
                                        ? t`Unignore User`
                                        : t`Ignore User`
                                }
                                onClick={onIgnoreMenuClick}
                                data-testid="ignore-user-menu-item"
                            />
                            {isFutureRlsEnabled && (
                                <MenuItem
                                    icon={<Trash height={20} width={20} />}
                                    text={t`Delete Conversation`}
                                    onClick={onDeleteConversationClick}
                                    color={MenuItemColor.Red}
                                    data-testid="delete-menu-item"
                                />
                            )}
                            {isReportEnabled && (
                                <MenuItem
                                    icon={<Report />}
                                    text={t`Report`}
                                    onClick={handleReportClick}
                                    data-testid="report-menu-item"
                                    className={styles.reportMenuItem}
                                    color={MenuItemColor.Red}
                                />
                            )}
                        </Menu>
                    </>
                )}
                {messagingView.isDesktopConversation && (
                    <>
                        {!isMinimized && (
                            <IconButton
                                icon={<MinimizeBlue width={15} height={20} />}
                                iconAlt={t`Minimize`}
                                onClick={onMinimize}
                                className={styles.menuIcon}
                                data-testid="minimize-button"
                            />
                        )}
                        {isMinimized && numUnread > 0 && (
                            <UnreadBadge
                                count={numUnread}
                                maxCount={9}
                                sizing="static"
                                className={styles.headerBadge}
                            />
                        )}
                        <IconButton
                            icon={<CloseBlue width={24} height={24} />}
                            iconAlt={t`Close`}
                            onClick={(e) => {
                                e.stopPropagation()
                                onCloseMessaging()
                            }}
                            className={styles.menuIcon}
                            data-testid="close-button"
                        />
                    </>
                )}
            </div>
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                message={t`Are you sure you want to delete this chat?`}
                confirmText={t`Delete`}
                cancelText={t`Cancel`}
                confirmButtonColor={ModalButtonColor.Delete}
            />
        </div>
    )
}
